import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Parser, Player } from 'svga.lite';
import SVGA from 'svgaplayerweb';
import constant from '../constants/constant';

export const resolveUrl = (url) => {
  if (!url) return '';
  if (typeof url === 'object') {
    if (url instanceof File || url instanceof Blob) {
      return URL.createObjectURL(url);
    }
    return '';
  }
  if (typeof url !== 'string') return String(url);
  const trimmed = url.trim();
  if (trimmed.startsWith('blob:') || trimmed.startsWith('data:') || trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  const baseOrigin = constant.API_BASE_URL ? constant.API_BASE_URL.replace(/\/api$/, '') : 'https://sandbox.xoxolite.com/xoxo';
  const cleanPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return `${baseOrigin}${cleanPath}`;
};

// Safe chunked ArrayBuffer to Base64 to prevent stack overflow on large assets
const arrayBufferToBase64 = (buffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const chunkSize = 16384;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }
  return window.btoa(binary);
};

const SvgaPlayer = ({ src, className = "w-full h-full object-contain", style = {}, isSvgExplicit = false }) => {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Robust detection for SVG (standard vector graphic) vs SVGA (binary animation)
  const isSvg = useMemo(() => {
    if (isSvgExplicit) return true;
    if (!src) return false;
    if (typeof src === 'object') {
      if (src.name && src.name.toLowerCase().endsWith('.svg')) return true;
      if (src.type && src.type.includes('svg')) return true;
      return false;
    }
    if (typeof src === 'string') {
      const clean = src.split('?')[0].split('#')[0].toLowerCase();
      if (clean.endsWith('.svg')) return true;
      if (src.startsWith('data:image/svg+xml')) return true;
    }
    return false;
  }, [src, isSvgExplicit]);

  const targetUrl = useMemo(() => resolveUrl(src), [src]);

  useEffect(() => {
    let player = null;
    let isMounted = true;

    if (!src || !targetUrl || isSvg) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(false);

    const loadSvga = async () => {
      try {
        const container = containerRef.current;
        if (!container) return;

        const parser = new Parser({ disableWorker: false });
        let arrayBuffer;
        if (typeof src === 'object' && typeof src.arrayBuffer === 'function') {
          arrayBuffer = await src.arrayBuffer();
        } else {
          const resp = await fetch(targetUrl, { mode: 'cors' });
          if (!resp.ok) throw new Error(`HTTP Error ${resp.status}`);
          arrayBuffer = await resp.arrayBuffer();
        }

        if (!isMounted) return;

        // Check if SVGA 1.0 (ZIP) or 2.0 (protobuf)
        const header = new Uint8Array(arrayBuffer, 0, 4);
        const isZip = header[0] === 80 && header[1] === 75 && header[2] === 3 && header[3] === 4;

        if (isZip) {
          // SVGA 1.0 fallback to svgaplayerweb
          container.innerHTML = '';
          player = new SVGA.Player(container);
          player.loops = 0;
          player.clearsAfterStop = false;
          player.setContentMode("AspectFit");
          const base64Data = arrayBufferToBase64(arrayBuffer);
          const dataUri = `data:svga/1.0;base64,${base64Data}`;
          const svgaParser = new SVGA.Parser();
          svgaParser.load(dataUri, (videoItem) => {
            if (isMounted && player) {
              player.setVideoItem(videoItem);
              player.startAnimation();
              setLoading(false);
            }
          }, () => {
            if (isMounted) { setError(true); setLoading(false); }
          });
          return;
        }

        // Modern SVGA 2.0 via svga.lite
        const canvas = document.createElement('canvas');
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        canvas.style.objectFit = 'contain';
        canvas.style.display = 'block';
        container.innerHTML = '';
        container.appendChild(canvas);

        const litePlayer = new Player(canvas);
        litePlayer.set({ loop: 0 });
        const svgaData = await parser.do(arrayBuffer);
        if (!isMounted) return;

        await litePlayer.mount(svgaData);
        if (!isMounted) return;

        litePlayer.start();
        player = litePlayer;
        setLoading(false);
      } catch (err) {
        console.warn("SVGA playback error:", targetUrl, err);
        // Fallback: attempt svgaplayerweb if svga.lite fails
        try {
          if (!isMounted) return;
          const container = containerRef.current;
          if (!container) return;
          container.innerHTML = '';
          const fallbackPlayer = new SVGA.Player(container);
          fallbackPlayer.loops = 0;
          fallbackPlayer.clearsAfterStop = false;
          fallbackPlayer.setContentMode("AspectFit");
          const fallbackParser = new SVGA.Parser();
          fallbackParser.load(targetUrl, (videoItem) => {
            if (isMounted) {
              fallbackPlayer.setVideoItem(videoItem);
              fallbackPlayer.startAnimation();
              player = fallbackPlayer;
              setLoading(false);
            }
          }, () => {
            if (isMounted) { setError(true); setLoading(false); }
          });
        } catch (fbErr) {
          if (isMounted) {
            setError(true);
            setLoading(false);
          }
        }
      }
    };

    loadSvga();

    return () => {
      isMounted = false;
      if (player) {
        try {
          if (typeof player.destroy === 'function') {
            player.destroy();
          } else if (typeof player.stopAnimation === 'function') {
            player.stopAnimation(true);
            player.clear();
          }
        } catch (e) { }
      }
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [src, targetUrl, isSvg]);

  // If passed an SVG image file, render clean, scalable vector image
  if (isSvg) {
    return (
      <div className={`relative flex items-center justify-center overflow-hidden ${className}`} style={style}>
        <img
          src={targetUrl}
          alt="SVG Asset"
          className="w-full h-full object-contain"
          onError={() => setError(true)}
        />
        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-2 rounded-xl bg-slate-50 text-slate-500 border border-slate-200">
            <span className="text-xs font-bold">SVG</span>
          </div>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center text-center p-2 rounded-xl bg-pink-50 text-pink-600 border border-pink-200 ${className}`} style={style}>
        <span className="text-xs font-black tracking-wider">SVGA</span>
        <span className="text-[9px] text-slate-500 font-bold">Animation</span>
      </div>
    );
  }

  return (
    <div className={`relative flex items-center justify-center overflow-hidden ${className}`} style={{ minWidth: '32px', minHeight: '32px', ...style }}>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900/10 backdrop-blur-[1px] rounded-xl z-10">
          <div className="w-5 h-5 border-2 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      <div
        ref={containerRef}
        className="w-full h-full flex items-center justify-center overflow-hidden"
        style={{ width: '100%', height: '100%', minWidth: '32px', minHeight: '32px' }}
      />
    </div>
  );
};

export default SvgaPlayer;
